//------------------------------------------------------------------------------
// BFA10102G 共架申込 基本情報
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, getFormValues } from 'redux-form';
import Grid from '@mui/material/Grid';

// 共通
import withRouter from '@/components/organisms/withRouter';
import AppLayout from '@/layouts/app-layout';
import PageTitle from '@/components/molecules/PageTitle';
import PoleItem from '@/utils/pole-item';
import MessageArea from '@/components/molecules/WarningMessageArea';
import commonOperations from '@/reducks/common/operations';
import backend from '@/common/backend';

// 共架申込
import OrderNumber from '../common/order-number';
import OrderInfoPanel from './order-info';
import OrderMemoPanel from './order-memo';
import OperationPanel from './operations';
import OrderAttachPanel from './order-attach';
import PoleSelectDialog from './pole-select';
import { orderDelete } from './order-delete';

// 定数
const FORM_NAME = 'BFA10102G';
const PAGE_NAME = '共架申込 基本情報';

// Grid子要素スタイル - 高さを揃える
const SX_ADJUST_HEIGHT = {

  // 要素自体をFlexコンテナにする
  display: 'flex',
  flexDirection: 'column',

  // 直下の要素の高さを100%にする
  '& > *': { height: '100%' },
};

/**
 * 共架申込 基本情報画面
 */
class OrderBase extends React.Component {
  /**
   * コンストラクタ
   */
  constructor(props) {
    super(props);

    // ステート
    this.state = {
      warnings: [],      // 警告メッセージ
      orderInfo: null,   // 申込情報
      orderPole: null,   // 代表電柱

      // 請求先一覧
      kojiHosyokinList: [],  // 工事補償金請求先
      gijyutuKentoList: [],  // 技術検討費請求先

      // 電柱選択ダイアログ
      openPoleSelect: false,
    };
  }

  /**
   * マウント
   */
  async componentDidMount() {

    // フォーム項目初期化
    this.props.initialize({});

    // 申込番号 (パスパラメーター)
    const orderNo = OrderNumber.from(this.props.params);

    // 申込取得 (申込番号有効)
    if (orderNo.valid()) {
      const res = await backend.get('/bfa10102g/load/' + orderNo.path());
      const body = this.parseResponse(res);
      this.setState({

        // 請求先一覧設定
        kojiHosyokinList: this.parseBills(body?.kojiHosyokinList),
        gijyutuKentoList: this.parseBills(body?.gijyutuKentoList),
      });
      return;
    }
    
    // 新規申込 (申込番号未設定)
    const res = await backend.get('/bfa10102g/init');
    const body = res.parse(this.props.doShowMessage).body;
    this.setState({
      warnings: res.messages,

      // 申込番号/申込情報
      orderInfo: res.ok ? { orderStatus: '' } : null,

      // 請求先一覧設定
      kojiHosyokinList: this.parseBills(body?.kojiHosyokinList),
      gijyutuKentoList: this.parseBills(body?.gijyutuKentoList),
    });
  }

  /**
   * 再マウント
   */
  componentDidUpdate(prevProps) {
    
    // 申込番号(URLパス)が変わったら申込情報再読み込み
    const orderNo = OrderNumber.from(this.props.params);
    const oldOrder = OrderNumber.from(prevProps.params);
    if (!orderNo.same(oldOrder)) {
      this.componentDidMount();
    }
  }

  /**
   * 描画
   */
  render() {
    const { values, change, handleSubmit } = this.props;
    const orderNo = OrderNumber.from(this.props.params);

    // 更新可否判定
    const orderStatus = this.state.orderInfo?.orderStatus;
    const savable =
        orderStatus === '' ||  // 新規
        orderStatus === '01';  // 作成中

    // 削除可否判定
    const deletable = orderStatus === '01';

    //
    // 描画
    //
    return (
      <AppLayout>

        {/* ページタイトル */}
        <PageTitle pageTitle={PAGE_NAME} />

        {/* メッセージ領域 */}
        <MessageArea messages={this.state.warnings}/>

        {/* 申込基本情報 */}
        <form noValidate autoComplete="off">
          <Grid container spacing={2}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'strech',
            }}
          >
            {/* 申込情報パネル - 1行目 */}
            <Grid item xs={12}>
              <OrderInfoPanel
                values={values}
                change={change}
                orderNo={orderNo}
                orderInfo={this.state.orderInfo}
                orderPole={this.state.orderPole}
                updatable={savable}
                kojiHosyokinList={this.state.kojiHosyokinList}
                gijyutuKentoList={this.state.gijyutuKentoList}
                handlePoleSelect={this.handleClickPoleSelect}
              />
            </Grid>

            {/* 添付パネル - 2行目/左側 */}
            <Grid item xs={7} sx={SX_ADJUST_HEIGHT}>
              <OrderAttachPanel
                updatable={savable}
              />
            </Grid>

            {/* メモパネル - 2行目/右側 */}
            <Grid item xs={5} sx={SX_ADJUST_HEIGHT}>
              <OrderMemoPanel
                updatable={savable}
              />
            </Grid>
          </Grid>
        </form>

        {/* 操作パネル */}
        <OperationPanel
          orderNo={orderNo}
          savable={savable}
          deletable={deletable}
          navigate={this.props.navigate}
          handleSave={handleSubmit(this.handleSave)}
          handleDelete={this.handleDelete}
        />

        {/* 電柱選択ダイアログ */}
        <PoleSelectDialog
          initialPole={this.state.orderPole}
          isDialogOpen={this.state.openPoleSelect}
          onDialogClose={this.handleClosePoleSelect}
        />
      </AppLayout>
    );
  }

  //----------------------------------------------------------------------------
  // イベントハンドラー
  //----------------------------------------------------------------------------
  /** 一時保存 */
  handleSave = async () => {
    const { values } = this.props; 
    const orderNo = OrderNumber.from(this.props.params);

    // 保存する申込情報
    const { kojiHosyokinBill, gijyutuKentoBill, ...saveValues } = values;

    // 代表電柱
    saveValues.kojiArea = this.state.orderPole?.areaCode;
    saveValues.kojiLine = this.state.orderPole?.lineCode;
    saveValues.kojiPole = this.state.orderPole?.poleNo;

    // 工事補償金請求先
    const kjTokens = kojiHosyokinBill?.split('/');
    saveValues.kjBillCompany  = kjTokens?.at(0);
    saveValues.kjBillZigyosya = kjTokens?.at(1);
    saveValues.kjBillCode     = kjTokens?.at(2);

    // 技術検討費用請求先
    const gkTokens = gijyutuKentoBill?.split('/');
    saveValues.gkBillCompany  = gkTokens?.at(0);
    saveValues.gkBillZigyosya = gkTokens?.at(1);
    saveValues.gkBillCode     = gkTokens?.at(2);

    // レコード更新時刻
    saveValues.updateTime = this.state.orderInfo?.updateTime;

    // 一時保存 (申込番号有効)
    if (orderNo.valid()) {
      console.info(FORM_NAME, `一時保存 order=${orderNo}`);
      const res = await backend.post('/bfa10102g/save/' + orderNo.path(), {
        orderInfo: saveValues,
        attchList: [],
      });

      // レスポンス解析
      this.parseResponse(res);
      return;
    }

    // 新規保存 (申込番号未設定)
    console.info(FORM_NAME, `新規保存 order=${orderNo}`);
    const res = await backend.post('/bfa10102g/save', {
      orderInfo: saveValues,
      attchList: [],
    });

    // レスポンス解析
    const body = res.parse(this.props.doShowMessage).body;
    this.setState({ warnings: res.messages });
    if (res.ok) {

      // 採番した申込番号でURL更新 (申込情報読み込み)
      const newOrder = OrderNumber.of(body.orderNo, body.rev);
      if (newOrder.valid()) {
        const path = '/bfa10102g/' + newOrder.path();
        this.props.navigate(path, { replace: true });
      }
    }
  }

  /** 削除 */
  handleDelete = () => {
    console.info(FORM_NAME, '申込削除');
    const orderNo = OrderNumber.from(this.props.params);
    orderDelete(orderNo,
        this.props.navigate,
        this.props.doShowMessage);
  }

  /** 電柱選択ボタン処理 */
  handleClickPoleSelect = () => {
    this.setState({ openPoleSelect: true });
  }

  /**
   * 電柱選択ダイアログ終了処理
   *
   * @param {PoleItem|null} selectedPole 選択した電柱
   *                        (キャンセル終了の場合はヌル)
   */
  handleClosePoleSelect = async (selectedPole) => {

    // ダイアログを閉じる
    this.setState({ openPoleSelect: false });

    // 電柱選択処理
    if (selectedPole) {
      const { change } = this.props;

      // 選択電柱が変わっていれば代表電柱を更新
      if (!selectedPole.same(this.state.orderPole)) {
        this.setState({ orderPole: selectedPole });

        // 電柱設置場所を検索
        const req = {
          area: selectedPole.areaCode,
          line: selectedPole.lineCode,
          pole: selectedPole.poleNo,
        };
        const res = await backend.post('/master/pole-place', req);
        const placeCode = res.ok ? res.body?.placeCode : null;
        const placeName = res.ok ? res.body?.placeName : null;

        // 工事場所住所を更新
        if (placeCode?.length === 11) {
          const prefectures = placeCode.substring(0, 2);
          const sikutyoson = placeCode.substring(0, 5) + '000000';
          const oazatusyo = (placeCode.substring(5, 8) === '000')
              ? null
              : placeCode.substring(0, 8) + '000';
          const azatyome = (placeCode.substring(8, 11) === '000')
              ? null
              : placeCode;
          change('kojiPrefectures',    prefectures);
          change('kojiMunicipalities', sikutyoson);
          change('kojiOazatusyo',      oazatusyo);
          change('kojiAzatyome',       azatyome);
          change('kojiBirumei',        placeName);
        }
      }
    }
  }

  //----------------------------------------------------------------------------
  // 内部メソッド
  //----------------------------------------------------------------------------
  /**
   * 請求先リスト解析
   *
   * @param bills 請求先リスト
   */
  parseBills(bills) {
    if (!Array.isArray(bills)) {
      return [];
    }
    return bills.map((bill) => ({
      value: [ bill.company, bill.zigyosya, bill.seikyusaki ].join('/'),
      label: [ bill.name, bill.tanto, bill.tel ].filter(Boolean).join(' '),
    }));
  }

  /**
   * レスポンス解析
   *
   * @param {object} res 申込レスポンス
   * @return レスポンスボディ
   */
  parseResponse(res) {
    const body = res.parse(this.props.doShowMessage).body;
    this.setState({ warnings: res.messages });
    if (res.ok) {

      // 申込情報更新
      const info = body.orderInfo ?? {};
      this.setState({
        orderInfo: info,

        // 代表電柱更新
        orderPole: new PoleItem({
          areaCode: info.kojiAreaCode,
          areaName: info.kojiAreaName,
          lineCode: info.kojiLineCode,
          lineName: info.kojiLineName,
          poleNo:   info.kojiPoleNo,
          poleKind: info.kojiPoleKind,
          latitude: info.kojiLatitude,
          longitude: info.kojiLongitude,
        }),
      });

      // 工事補償金請求先
      const kojiHosyokinBill = [
        info.kjBillCompany,
        info.kjBillZigyosya,
        info.kjBillCode,
      ].join('/');

      // 技術検討費用請求先
      const gijyutuKentoBill = [
        info.gkBillCompany,
        info.gkBillZigyosya,
        info.gkBillCode,
      ].join('/');

      // フォーム項目
      const formValues = {
        kgOrderNo:          info.kgOrderNo,           // 共架者申込番号
        kojiKenmei:         info.kojiKenmei,          // 工事件名
        kojiPrefectures:    info.kojiPrefectures,     // 工事場所/都道府県
        kojiMunicipalities: info.kojiMunicipalities,  // 工事場所/市区町村
        kojiOazatusyo:      info.kojiOazatusyo,       // 工事場所/大字通称
        kojiAzatyome:       info.kojiAzatyome,        // 工事場所/字丁目
        kojiBirumei:        info.kojiBirumei,         // 工事場所/ビル名等
        tyakkoYoteiDate:    info.tyakkoYoteiDate,     // 着工予定日
        syunkoYoteiDate:    info.syunkoYoteiDate,     // しゅん工予定日
        autoOrder:        !!info.autoOrder,           // 自動で本申込

        // 請求先
        kojiHosyokinBill: kojiHosyokinBill,   // 工事補償金請求先
        gijyutuKentoBill: gijyutuKentoBill,   // 技術検討費用請求先

        // メモ欄
        memo: info.memo,

        // 添付情報
        attachList: body.attachList ?? [],
      };

      // フォーム項目更新
      this.props.initialize(formValues);
    }
    return body;
  }
}

// プロパティ
OrderBase.propTypes = {

  // フォーム
  change: PropTypes.func,
  values: PropTypes.object,
  initialize: PropTypes.func,
  handleSubmit: PropTypes.func,

  // ルーティング
  params: PropTypes.object,
  navigate: PropTypes.func,

  // ストア
  doShowMessage: PropTypes.func,
};

// フォーム接続
OrderBase = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: true,
})(connect((state) => ({
  values: getFormValues(FORM_NAME)(state),
}))(OrderBase));

// ルーティング
OrderBase = withRouter(OrderBase);

// ストア接続
const mapStateToProps = (state) => ({
});
const mapDispatchToProps = {
  doShowMessage: commonOperations.doShowMessage,
};
OrderBase = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderBase);

// エクスポート
export default OrderBase;
